/**
 * Collision-free label placement for dense visualisations (bubble map,
 * chart markers). Pure functions in screen pixels so they're testable and
 * shared by the canvas and the chart.
 *
 * Greedy by priority: the most important label claims its spot first; each
 * later label tries its candidate positions in order and is dropped if every
 * one collides with an already placed label, an obstacle box, or (for labels
 * outside their own bubble) a neighbouring bubble.
 */
const overlaps = (a, b, pad) => a.x < b.x + b.w + pad && a.x + a.w + pad > b.x && a.y < b.y + b.h + pad && a.y + a.h + pad > b.y;
const boxHitsCircle = (b, c) => {
    const nx = Math.max(b.x, Math.min(c.x, b.x + b.w));
    const ny = Math.max(b.y, Math.min(c.y, b.y + b.h));
    return (nx - c.x) ** 2 + (ny - c.y) ** 2 < c.r * c.r;
};
/** Does a label of this size fit inside a circle of radius r (with a margin)? */
export const fitsInside = (w, h, r, margin = 3) => Math.hypot(w / 2, h / 2) + margin <= r;
export function layoutLabels(requests, o) {
    const pad = o.pad ?? 3;
    const gap = o.gap ?? 4;
    const slots = o.slots ?? ['inside', 'above', 'below', 'right', 'left'];
    const placed = [];
    const taken = [...(o.obstacles ?? [])];
    const circles = o.circles ?? [];
    const max = o.max ?? Infinity;
    const order = [...requests].sort((a, b) => Number(Boolean(b.force)) - Number(Boolean(a.force)) || b.priority - a.priority);
    for (const q of order) {
        if (placed.length >= max && !q.force)
            break;
        let best;
        for (const slot of slots) {
            if (slot === 'inside' && !fitsInside(q.w, q.h, q.r))
                continue;
            const [cx, cy] = slot === 'inside'
                ? [q.x, q.y]
                : slot === 'above'
                    ? [q.x, q.y - q.r - gap - q.h / 2]
                    : slot === 'below'
                        ? [q.x, q.y + q.r + gap + q.h / 2]
                        : slot === 'right'
                            ? [q.x + q.r + gap + q.w / 2, q.y]
                            : [q.x - q.r - gap - q.w / 2, q.y];
            const box = { x: cx - q.w / 2, y: cy - q.h / 2, w: q.w, h: q.h };
            if (box.x < 0 || box.y < 0 || box.x + box.w > o.width || box.y + box.h > o.height)
                continue;
            if (taken.some((t) => overlaps(box, t, pad)))
                continue;
            // An outside label must not sit on top of another bubble; inside labels
            // only need to avoid other labels.
            if (slot !== 'inside' && circles.some((c) => c.id !== q.id && boxHitsCircle(box, c)))
                continue;
            best = { id: q.id, slot, cx, cy, box };
            break;
        }
        if (!best && q.force) {
            const cy = q.y - q.r - gap - q.h / 2;
            best = { id: q.id, slot: 'above', cx: q.x, cy, box: { x: q.x - q.w / 2, y: cy - q.h / 2, w: q.w, h: q.h } };
        }
        if (best) {
            placed.push(best);
            taken.push(best.box);
        }
    }
    return placed;
}
/** Share of supply for a label: no false "0.00%" for dust, fewer digits for big holders. */
export function pctLabel(p) {
    if (!(p > 0))
        return '0%';
    if (p < 0.01)
        return '<0.01%';
    if (p < 1)
        return `${p.toFixed(2)}%`;
    if (p < 10)
        return `${p.toFixed(1)}%`;
    return `${p.toFixed(0)}%`;
}
//# sourceMappingURL=labels.js.map